import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

const Portfolio: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [portfolioItems, setPortfolioItems] = useState<Array<{_id: string; category: string; imageUrl: string; title: string; link?: string}>>([]);

  useEffect(() => {
    fetch(`${API_URL}/api/photos`)
      .then(res => res.json())
      .then(data => setPortfolioItems(data));
  }, []);

  const categories = [
    { id: 'all', name: 'All Work' },
    { id: 'portraits', name: 'Portraits' },
    { id: 'weddings', name: 'Weddings' },
    { id: 'fashion', name: 'Fashion' },
    { id: 'events', name: 'Events' },
    { id: 'products', name: 'Products' }
  ];

  const filteredItems = activeCategory === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === activeCategory);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-black text-white">
        <div className="section-padding text-center">
          <h1 className="font-serif text-5xl md:text-6xl mb-6">Portfolio</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            A curated collection of our finest work, showcasing the artistry and 
            technical excellence that defines our studio.
          </p>
        </div>
      </section>

      {/* Filter Navigation */}
      <section className="py-12 bg-white border-b">
        <div className="section-padding">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-2 font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-black text-white'
                    : 'text-gray-600 hover:text-black border border-gray-300 hover:border-black'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-white">
        <div className="section-padding">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
            {filteredItems.map((item, index) => (
              <div 
                key={item._id} 
                className="group cursor-pointer animate-fade-in break-inside-avoid mb-4"
                style={{animationDelay: `${index * 0.1}s`}}
                onClick={() => item.link && window.open(item.link, '_blank')}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={item.imageUrl} 
                    alt={item.title}
                    className="w-full h-auto object-cover transition-all duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                  <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <h3 className="font-serif text-xl">{item.title}</h3>
                    <p className="text-sm text-gray-300 capitalize">{item.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-studio-gray">
        <div className="section-padding text-center">
          <h2 className="font-serif text-4xl mb-6">Inspired by Our Work?</h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Let's create something beautiful together. Contact us to discuss your project 
            and discover how we can bring your vision to life.
          </p>
          <div className="space-x-4">
            <a href="/booking" className="btn-primary">
              Book a Session
            </a>
            <a href="/contact" className="border border-black text-black px-8 py-3 font-medium tracking-wide hover:bg-black hover:text-white transition-all duration-300">
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
